// ═══════════════════════════════════════════════════════════════
// PresenceManager.js — Heartbeat-based agent online/offline detection
// Agents ping periodically; no ping within timeout = offline
// ═══════════════════════════════════════════════════════════════

import { PATHS, AGENT_STATUS } from './HelpCenterConfig.js';
import { resolveRecordScope, presenceBucket, presenceWatchBuckets } from './recordScope.js';

// Flattens { [bucket]: { [agentId]: record } } into agent records, newest group wins.
function flattenBuckets(buckets) {
  const merged = {};
  for (const group of Object.values(buckets || {})) {
    if (group && typeof group === 'object') Object.assign(merged, group);
  }
  return merged;
}

/**
 * PresenceManager handles agent availability detection.
 *
 * Agent side:
 *   const pm = new PresenceManager(storageAdapter, config);
 *   pm.goOnline(agentUser);    // starts heartbeat
 *   pm.goAway(agentUser);      // sets away status, keeps heartbeat
 *   pm.goOffline(agentUser);   // stops heartbeat, removes presence
 *
 * Consumer side (checking availability):
 *   const agents = await pm.getOnlineAgents();
 *   const available = await pm.isAnyAgentAvailable();
 *   const unsub = pm.subscribeToPresence(callback);  // real-time updates
 */
class PresenceManager {
  constructor(storageAdapter, config = {}) {
    this._storage = storageAdapter;
    this._heartbeatInterval = config.heartbeatIntervalMs || 30000;
    this._presenceTimeout = config.presenceTimeoutMs || 60000;
    this._timers = new Map(); // agentId → intervalId
  }

  /**
   * Set agent status to online and start heartbeat pings.
   * @param {Object} agent — { id, name, email, role }
   */
  goOnline(agent) {
    if (!agent?.id) return;
    this._stopHeartbeat(agent.id);
    this._writePresence(agent, AGENT_STATUS.ONLINE);
    const intervalId = setInterval(() => {
      this._writePresence(agent, AGENT_STATUS.ONLINE);
    }, this._heartbeatInterval);
    this._timers.set(agent.id, intervalId);
  }

  /**
   * Set agent status to away (still pinging, but shown as away).
   * @param {Object} agent — { id, name, email, role }
   */
  goAway(agent) {
    if (!agent?.id) return;
    // Keep heartbeat running but change status
    this._writePresence(agent, AGENT_STATUS.AWAY);
    // If no heartbeat is running, start one
    if (!this._timers.has(agent.id)) {
      const intervalId = setInterval(() => {
        this._writePresence(agent, AGENT_STATUS.AWAY);
      }, this._heartbeatInterval);
      this._timers.set(agent.id, intervalId);
    }
  }

  /**
   * Set agent status to offline and stop heartbeat.
   * @param {Object} agent — { id, name, email, role }
   */
  goOffline(agent) {
    if (!agent?.id) return;
    this._stopHeartbeat(agent.id);
    this._writePresence(agent, AGENT_STATUS.OFFLINE);
  }

  /**
   * Clean up all heartbeat timers (call on unmount / logout).
   * @param {Object} agent — { id }
   */
  destroy(agent) {
    if (agent?.id) {
      this._stopHeartbeat(agent.id);
      // Write offline status before destroying
      this._writePresence(agent, AGENT_STATUS.OFFLINE).catch(() => {});
    } else {
      // Stop all timers
      for (const [agentId, intervalId] of this._timers) {
        clearInterval(intervalId);
      }
      this._timers.clear();
    }
  }

  /**
   * Get all agents currently considered online or away.
   * Filters out agents whose last heartbeat exceeds the timeout.
   * @returns {Array} — [{ id, name, email, status, lastSeen }]
   */
  async getOnlineAgents() {
    const buckets = presenceWatchBuckets(await resolveRecordScope(this._storage, null));
    let presenceData;
    if (!buckets) {
      presenceData = flattenBuckets(await this._storage.get(PATHS.agentPresence()));
    } else {
      const groups = await Promise.all(buckets.map(b => this._storage.get(PATHS.agentPresence(b)).catch(() => null)));
      presenceData = flattenBuckets(groups);
    }
    return this._onlineAgents(presenceData);
  }

  // Agent is considered online if last heartbeat is within timeout
  // and their status is not explicitly offline
  _onlineAgents(presenceData) {
    const now = Date.now();
    const agents = [];
    for (const [agentId, record] of Object.entries(presenceData || {})) {
      if (!record || !record.lastSeen) continue;
      const elapsed = now - new Date(record.lastSeen).getTime();
      if (elapsed <= this._presenceTimeout && record.status !== AGENT_STATUS.OFFLINE) {
        agents.push({ id: agentId, name: record.name || 'Support Agent', status: record.status, lastSeen: record.lastSeen });
      }
    }
    return agents;
  }

  /**
   * Check if at least one agent is available (online, not just away).
   * @returns {boolean}
   */
  async isAnyAgentAvailable() {
    const agents = await this.getOnlineAgents();
    return agents.some(a => a.status === AGENT_STATUS.ONLINE);
  }

  /**
   * Check if at least one agent is online or away.
   * @returns {boolean}
   */
  async isAnyAgentPresent() {
    const agents = await this.getOnlineAgents();
    return agents.length > 0;
  }

  /**
   * Subscribe to real-time presence changes.
   * Callback receives the full presence map (filtered by timeout).
   * @param {Function} callback — (agents: Array) => void
   * @returns {Function} unsubscribe
   */
  subscribeToPresence(callback) {
    let closed = false;
    const unsubs = [];
    resolveRecordScope(this._storage, null).then((scope) => {
      if (closed) return;
      const buckets = presenceWatchBuckets(scope);
      if (!buckets) {
        unsubs.push(this._storage.subscribe(PATHS.agentPresence(), data => callback(this._onlineAgents(flattenBuckets(data)))));
        return;
      }
      const latest = {};
      for (const bucket of buckets) {
        unsubs.push(this._storage.subscribe(PATHS.agentPresence(bucket), (data) => {
          latest[bucket] = data;
          callback(this._onlineAgents(flattenBuckets(latest)));
        }));
      }
    }).catch(() => callback([]));
    return () => {
      closed = true;
      unsubs.forEach(unsub => { if (typeof unsub === 'function') unsub(); });
    };
  }

  // ── Internal ──

  async _writePresence(agent, status) {
    try {
      const bucket = presenceBucket(await resolveRecordScope(this._storage, agent));
      if (!bucket) return;
      await this._storage.set(PATHS.agentStatus(bucket, agent.id), {
        name: agent.name || 'Support Agent',
        status,
        lastSeen: new Date().toISOString(),
      });
    } catch (e) {
      console.error('[PresenceManager] Failed to write presence:', e);
    }
  }

  _stopHeartbeat(agentId) {
    const intervalId = this._timers.get(agentId);
    if (intervalId) {
      clearInterval(intervalId);
      this._timers.delete(agentId);
    }
  }
}

export default PresenceManager;
